/**
 * CRM · Contactos — LISTA UNIFICADA de clientes. Un contacto/cliente es una
 * PERSONA (individuo/autónomo/freelance) o una EMPRESA (con sus personas de
 * contacto dentro). Conviven Leads y Clientes, de todas las fuentes
 * (ecommerce/marketplace/manual/api). Los clientes de ecommerce sin ficha CRM
 * aparecen con su origen y métricas; al abrirlos se crea su ficha.
 * Botón "Enriquecer con IA" en la ficha (analiza el historial). Gated por
 * crm:read/manage + flag config.management.crm.enabled (opt-in).
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PlusIcon, TrashIcon, UserGroupIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common';
import {
  crmApi, type CrmContact, type CrmDirectoryItem, type ContactSource, type ContactKind, type CrmAiEnrichment,
  type CrmProposal, type CrmProposalLine, type ProposalStatus,
  type CrmCommunication, type CommunicationType, type CommunicationDirection,
  type CrmActivityEntry, type CrmActivityResponse,
} from '@/services/api';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';
import RolesPicker from '@/components/crm/RolesPicker';
import LeadEntityFields from '@/components/crm/LeadEntityFields';
import LeadProjectsSection from '@/components/crm/LeadProjectsSection';
import LeadLinkSection from '@/components/crm/LeadLinkSection';
import LeadCaptureSettingsModal from '@/components/crm/LeadCaptureSettingsModal';

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
const cardClass = 'bg-white rounded-lg border border-gray-200 shadow-sm';

const SOURCE_LABEL: Record<ContactSource, string> = {
  ecommerce: 'Ecommerce', marketplace: 'Marketplace', manual: 'Manual', api_import: 'API', web_form: 'Formulario web', other: 'Otro',
};
const SOURCE_COLOR: Record<ContactSource, string> = {
  ecommerce: 'bg-green-100 text-green-700', marketplace: 'bg-purple-100 text-purple-700',
  manual: 'bg-blue-100 text-blue-700', api_import: 'bg-amber-100 text-amber-700', web_form: 'bg-teal-100 text-teal-700', other: 'bg-gray-100 text-gray-600',
};
const SOURCES: ContactSource[] = ['ecommerce', 'marketplace', 'manual', 'api_import', 'web_form', 'other'];
const TYPE_LABEL: Record<CrmDirectoryItem['type'], string> = { lead: 'Lead', prospect: 'Prospecto', customer: 'Cliente' };
const TYPE_COLOR: Record<CrmDirectoryItem['type'], string> = {
  lead: 'bg-gray-100 text-gray-600', prospect: 'bg-sky-100 text-sky-700', customer: 'bg-vida-100 text-vida-700',
};

const displayName = (c: { kind: ContactKind; companyName?: string; first_name?: string; last_name?: string; email?: string }) =>
  c.kind === 'company'
    ? (c.companyName || '(empresa sin nombre)')
    : ([c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || '(sin nombre)');

type EditState =
  | { mode: 'edit'; contact: CrmContact }
  | { mode: 'new'; kind?: ContactKind; prefill?: Partial<CrmContact> }
  | null;

export default function CrmContacts() {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const canManage = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.CRM_MANAGE));

  const [searchParams] = useSearchParams();
  const paramType = searchParams.get('type');
  const initialType: CrmDirectoryItem['type'] | 'all' =
    paramType === 'customer' || paramType === 'lead' ? paramType : 'all';

  const [items, setItems] = useState<CrmDirectoryItem[]>([]);
  const [companies, setCompanies] = useState<CrmContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState<CrmDirectoryItem['type'] | 'all'>(initialType);
  const [kind, setKind] = useState<ContactKind | 'all'>('all');
  const [source, setSource] = useState<ContactSource | 'all'>('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<EditState>(null);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [history, setHistory] = useState<EditState[]>([]); // navegación entre fichas

  const openContactById = async (id: string) => {
    try {
      const res = await crmApi.getContact(id);
      setHistory((h) => [...h, editing]);
      setEditing({ mode: 'edit', contact: res.data.contact });
    } catch { toast.error('No se pudo abrir la ficha'); }
  };
  const back = () => {
    if (history.length === 0) { setEditing(null); return; }
    setEditing(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
  };
  const closeAll = () => { setEditing(null); setHistory([]); };

  const load = async () => {
    setLoading(true);
    try {
      const [res, comps] = await Promise.all([
        crmApi.listContacts({
          limit: 500,
          type: type === 'all' ? undefined : type,
          kind: kind === 'all' ? undefined : kind,
          source: source === 'all' ? undefined : source,
          search: search || undefined,
        }),
        crmApi.listContacts({ kind: 'company', limit: 500 }).catch(() => ({ data: { contacts: [] } })),
      ]);
      setItems(res.data.contacts || []);
      setCompanies((comps.data.contacts || []) as unknown as CrmContact[]);
    } catch (e) {
      // 403 = CRM no activado en esta org (p.ej. tras cambiar de organización).
      if ((e as { response?: { status?: number } })?.response?.status !== 403) {
        toast.error('No se pudieron cargar los contactos');
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId, type, kind, source]);

  // Apertura directa desde otra vista (p.ej. Pipeline): ?open=<id> abre la ficha,
  // ?new=lead|customer abre el alta de una nueva persona con ese tipo.
  const didInitParams = useRef(false);
  useEffect(() => {
    if (didInitParams.current) return;
    didInitParams.current = true;
    const openId = searchParams.get('open');
    const newParam = searchParams.get('new');
    if (openId) { void openContactById(openId); return; }
    if (newParam) {
      setEditing({
        mode: 'new', kind: 'person',
        prefill: { kind: 'person', type: newParam === 'customer' ? 'customer' : 'lead', source: 'manual' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = async (item: CrmDirectoryItem) => {
    setHistory([]);
    if (!item.customerOnly) {
      try {
        const res = await crmApi.getContact(item._id);
        setEditing({ mode: 'edit', contact: res.data.contact });
      } catch { toast.error('No se pudo abrir el contacto'); }
    } else {
      // Cliente ecommerce sin ficha CRM → abrir para crear su ficha (persona).
      setEditing({
        mode: 'new', kind: 'person',
        prefill: {
          kind: 'person', customerId: item.customerId, source: 'ecommerce', type: 'customer',
          first_name: item.first_name, last_name: item.last_name, email: item.email, phone: item.phone,
        },
      });
    }
  };

  const remove = async (item: CrmDirectoryItem) => {
    if (item.customerOnly) return;
    if (!confirm(`¿Borrar "${displayName(item)}"?`)) return;
    try { await crmApi.removeContact(item._id); toast.success('Borrado'); void load(); }
    catch { toast.error('No se pudo borrar'); }
  };

  const money = (n?: number, cur = 'EUR') => (n === undefined ? '' : new Intl.NumberFormat('es-ES', { style: 'currency', currency: cur }).format(n));

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <UserGroupIcon className="w-7 h-7 text-vida-600" /> Contactos
          </h1>
          <p className="text-sm text-gray-500 mt-1">Personas y empresas · leads y clientes · de todas las fuentes.</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCaptureOpen(true)} className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
              Captación
            </button>
            <button onClick={() => setEditing({ mode: 'new', kind: 'person' })} className="inline-flex items-center gap-2 px-3 py-2 border border-vida-300 text-vida-700 rounded-lg text-sm font-medium hover:bg-vida-50">
              <PlusIcon className="w-5 h-5" /> Persona
            </button>
            <button onClick={() => setEditing({ mode: 'new', kind: 'company' })} className="inline-flex items-center gap-2 px-3 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700">
              <PlusIcon className="w-5 h-5" /> Empresa
            </button>
          </div>
        )}
      </div>

      {/* Filtro PRIMARIO por tipo + subfiltros (persona/empresa, origen) + búsqueda */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {([['all', 'Todos'], ['customer', 'Clientes'], ['lead', 'Leads']] as [CrmDirectoryItem['type'] | 'all', string][]).map(([t, label]) => (
            <button key={t} onClick={() => setType(t)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${type === t ? 'bg-white text-vida-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>
        <select className="rounded-lg border-gray-300 text-sm py-1.5 focus:ring-vida-500 focus:border-vida-500" value={kind} onChange={(e) => setKind(e.target.value as ContactKind | 'all')}>
          <option value="all">Personas y empresas</option>
          <option value="person">Solo personas</option>
          <option value="company">Solo empresas</option>
        </select>
        <select className="rounded-lg border-gray-300 text-sm py-1.5 focus:ring-vida-500 focus:border-vida-500" value={source} onChange={(e) => setSource(e.target.value as ContactSource | 'all')}>
          <option value="all">Todos los orígenes</option>
          {SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
        </select>
        <form onSubmit={(e) => { e.preventDefault(); void load(); }} className="flex-1 min-w-[160px]">
          <input className={inputClass} placeholder="Buscar…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </form>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><LoadingSpinner /></div>
      ) : items.length === 0 ? (
        <div className={`${cardClass} p-10 text-center text-gray-500`}>No hay contactos.</div>
      ) : (
        <div className={`${cardClass} divide-y divide-gray-100`}>
          {items.map((c) => (
            <div key={`${c._id}`} onClick={() => openEdit(c)}
              className="flex items-center justify-between gap-4 p-4 hover:bg-gray-50 cursor-pointer transition-colors">
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {c.kind === 'company' ? '🏢 ' : ''}{displayName(c)}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {c.email}{c.phone ? ` · ${c.phone}` : ''}
                  {c.kind === 'person' && c.companyName ? ` · ${c.companyName}` : ''}
                  {c.total_orders ? ` · ${c.total_orders} pedidos (${money(c.total_spent)})` : ''}
                  {c.customerOnly ? ' · sin ficha CRM' : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{c.kind === 'company' ? 'Empresa' : 'Persona'}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLOR[c.type]}`}>{TYPE_LABEL[c.type]}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_COLOR[c.source]}`}>{SOURCE_LABEL[c.source]}</span>
                {canManage && !c.customerOnly && (
                  <button onClick={() => remove(c)} className="p-1.5 text-gray-400 hover:text-red-600" title="Borrar"><TrashIcon className="w-5 h-5" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {captureOpen && <LeadCaptureSettingsModal onClose={() => setCaptureOpen(false)} />}
      {editing && (
        <ContactModal
          key={(editing.mode === 'edit' ? editing.contact._id : 'new') + '-' + history.length}
          state={editing}
          companies={companies}
          canBack={history.length > 0}
          onBack={back}
          onOpenContact={openContactById}
          onClose={closeAll}
          onSaved={() => { closeAll(); void load(); }}
        />
      )}
    </div>
  );
}

function ContactModal({ state, companies, canBack, onBack, onOpenContact, onClose, onSaved, hideAi }: {
  state: Exclude<EditState, null>; companies: CrmContact[];
  canBack: boolean; onBack: () => void; onOpenContact: (id: string) => void;
  onClose: () => void; onSaved: () => void; hideAi?: boolean;
}) {
  const isEdit = state.mode === 'edit';
  const base = isEdit ? state.contact : (state.prefill || {});
  const kind: ContactKind = (isEdit ? state.contact.kind : (state.kind || base.kind || 'person'));
  const [form, setForm] = useState({
    first_name: base.first_name || '', last_name: base.last_name || '', position: base.position || '',
    companyName: base.companyName || '', taxId: base.taxId || '', website: base.website || '',
    email: base.email || '', phone: base.phone || '', type: (base.type || 'lead') as CrmContact['type'],
    source: (base.source || 'manual') as ContactSource, sourceRef: base.sourceRef || '',
    companyId: base.companyId || '', notes: base.notes || '',
    roles: base.roles || [],
    entity: base.entity || {},
    commercialContacts: base.commercialContacts || [],
  });
  const customerId = isEdit ? state.contact.customerId : (state.prefill?.customerId ?? null);
  const [busy, setBusy] = useState(false);

  // Personas de una empresa (solo al editar una empresa).
  const [persons, setPersons] = useState<CrmContact[]>([]);
  useEffect(() => {
    if (isEdit && kind === 'company') {
      crmApi.listContacts({ kind: 'person', companyId: state.contact._id, limit: 200 })
        .then((r) => setPersons((r.data.contacts || []) as unknown as CrmContact[]))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Enriquecido IA.
  const existingAi = state.mode === 'edit' ? state.contact.customFields?.ai : undefined;
  const [ai, setAi] = useState<CrmAiEnrichment | null>(
    existingAi ? { resumen: existingAi.summary || '', segmento: existingAi.segment || '', intereses: existingAi.interests || [], recomendaciones: existingAi.recommendations || [] } : null
  );
  const [aiBusy, setAiBusy] = useState(false);

  const payload = () => ({
    kind,
    ...form,
    companyId: kind === 'person' ? (form.companyId || null) : null,
    customerId: customerId || null,
  });

  const submit = async () => {
    setBusy(true);
    try {
      if (isEdit) await crmApi.updateContact(state.contact._id, payload());
      else await crmApi.createContact(payload());
      toast.success(isEdit ? 'Guardado' : 'Creado');
      onSaved();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo guardar';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const convertToCustomer = async () => {
    if (state.mode !== 'edit') return;
    setBusy(true);
    try { await crmApi.updateContact(state.contact._id, { type: 'customer' }); toast.success('Convertido en cliente'); onSaved(); }
    catch { toast.error('No se pudo convertir'); } finally { setBusy(false); }
  };

  // Enriquecer con IA: si es una ficha nueva (p.ej. cliente ecommerce), la crea primero.
  const enrich = async () => {
    setAiBusy(true);
    try {
      let id: string;
      if (state.mode === 'edit') id = state.contact._id;
      else {
        const created = await crmApi.createContact(payload());
        id = created.data.contact._id;
      }
      const res = await crmApi.enrich(id);
      setAi(res.data.ai);
      toast.success('Enriquecido con IA');
      if (state.mode !== 'edit') { onSaved(); }
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo enriquecer';
      toast.error(msg);
    } finally { setAiBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className={`${cardClass} w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto`}>
        {canBack && (
          <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-flex items-center gap-1">
            ← Volver
          </button>
        )}
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {isEdit ? 'Editar' : 'Nuevo'} {kind === 'company' ? 'empresa' : 'contacto'}
        </h2>
        {customerId && <p className="text-xs text-green-700 bg-green-50 rounded px-2 py-1 mb-3 inline-block">Enlazado a un cliente de ecommerce</p>}

        <div className="grid grid-cols-2 gap-3 mt-2">
          {kind === 'company' ? (
            <>
              <div className="col-span-2"><label className={labelClass}>Nombre de la empresa</label><input className={inputClass} value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} /></div>
              <div><label className={labelClass}>NIF/CIF</label><input className={inputClass} value={form.taxId} onChange={(e) => setForm({ ...form, taxId: e.target.value })} /></div>
              <div><label className={labelClass}>Web</label><input className={inputClass} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
            </>
          ) : (
            <>
              <div><label className={labelClass}>Nombre</label><input className={inputClass} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div><label className={labelClass}>Apellidos</label><input className={inputClass} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
              <div className="col-span-2"><label className={labelClass}>Cargo</label><input className={inputClass} value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></div>
              <div className="col-span-2">
                <label className={labelClass}>Empresa (opcional — vacío = autónomo/particular)</label>
                <select className={inputClass} value={form.companyId} onChange={(e) => setForm({ ...form, companyId: e.target.value })}>
                  <option value="">(ninguna)</option>
                  {companies.map((k) => <option key={k._id} value={k._id}>{k.companyName || '(sin nombre)'}</option>)}
                </select>
              </div>
            </>
          )}
          <div><label className={labelClass}>Email</label><input className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><label className={labelClass}>Teléfono</label><input className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div>
            <label className={labelClass}>Tipo</label>
            <select className={inputClass} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as CrmContact['type'] })}>
              <option value="lead">Lead</option><option value="customer">Cliente</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Origen</label>
            <select className={inputClass} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value as ContactSource })}>
              {SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
            </select>
          </div>
          {form.source !== 'manual' && form.source !== 'ecommerce' && (
            <div className="col-span-2"><label className={labelClass}>Ref. origen</label><input className={inputClass} value={form.sourceRef} onChange={(e) => setForm({ ...form, sourceRef: e.target.value })} /></div>
          )}
          <div className="col-span-2"><label className={labelClass}>Notas</label><textarea className={inputClass} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="col-span-2">
            <label className={labelClass}>Roles (papel en el ecosistema, no son permisos)</label>
            <RolesPicker value={form.roles} onChange={(roles) => setForm({ ...form, roles })} />
          </div>
        </div>

        {/* Alta básica de la entidad + contacto comercial (ALTA LEAD, VIDA-355) */}
        {kind === 'company' && (
          <LeadEntityFields
            entity={form.entity} commercialContacts={form.commercialContacts}
            onEntityChange={(entity) => setForm({ ...form, entity })}
            onCommercialChange={(commercialContacts) => setForm({ ...form, commercialContacts })}
          />
        )}

        {/* Personas de la empresa */}
        {isEdit && kind === 'company' && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <p className="text-sm font-semibold text-gray-900 mb-2">Personas de contacto ({persons.length})</p>
            {persons.length === 0 ? (
              <p className="text-xs text-gray-400">Aún no hay personas. Créalas como Persona y asígnalas a esta empresa.</p>
            ) : (
              <ul className="text-sm text-gray-700 space-y-0.5">
                {persons.map((p) => (
                  <li key={p._id} onClick={() => onOpenContact(p._id)}
                    className="flex justify-between items-center cursor-pointer hover:bg-gray-50 rounded px-2 py-1">
                    <span>{[p.first_name, p.last_name].filter(Boolean).join(' ') || p.email}</span>
                    <span className="text-xs text-gray-400">{p.position}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Enriquecido IA (para clientes/personas con historial) */}
        {kind === 'person' && !hideAi && (
          <div className="mt-5 border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><SparklesIcon className="w-5 h-5 text-vida-600" /> Análisis IA</h3>
              <button onClick={enrich} disabled={aiBusy} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-vida-700 border border-vida-300 rounded-lg hover:bg-vida-50 disabled:opacity-50">
                {aiBusy ? 'Analizando…' : ai ? 'Volver a analizar' : 'Enriquecer con IA'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Analiza el historial del cliente (pedidos + datos) y amplía su ficha: resumen, segmento, intereses y recomendaciones. Consume cuota de IA.</p>
            {ai && (
              <div className="mt-3 space-y-3 text-sm">
                {ai.resumen && <p className="text-gray-700 whitespace-pre-wrap">{ai.resumen}</p>}
                {ai.segmento && <p><span className="font-medium text-gray-700">Segmento:</span> <span className="px-2 py-0.5 rounded-full text-xs bg-vida-100 text-vida-700">{ai.segmento}</span></p>}
                {ai.intereses?.length ? (
                  <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Intereses</p><div className="flex flex-wrap gap-1">{ai.intereses.map((it, i) => <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">{it}</span>)}</div></div>
                ) : null}
                {ai.recomendaciones?.length ? (
                  <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Recomendaciones de venta</p><ul className="list-disc list-inside text-gray-700 space-y-0.5">{ai.recomendaciones.map((r, i) => <li key={i}>{r}</li>)}</ul></div>
                ) : null}
              </div>
            )}
          </div>
        )}

        {/* Actividad unificada (B4): métricas ecommerce + citas del planificador */}
        {isEdit && <ActivitySection contactId={state.contact._id} />}

        {/* Proyectos del lead (ALTA LEAD 2.1) */}
        {isEdit && <LeadProjectsSection contactId={state.contact._id} />}

        {/* Enlace personal del lead (ALTA LEAD 1.3) */}
        {isEdit && state.contact.type !== 'customer' && <LeadLinkSection contactId={state.contact._id} hasEmail={!!state.contact.email} />}

        {/* Propuestas comerciales (B2) — solo sobre una ficha ya existente */}
        {isEdit && <ProposalsSection contactId={state.contact._id} />}

        {/* Timeline de comunicaciones (B3) */}
        {isEdit && <CommunicationsSection contactId={state.contact._id} />}

        <div className="flex items-center justify-between mt-6">
          <div>
            {isEdit && state.contact.type === 'lead' && (
              <button onClick={convertToCustomer} disabled={busy} className="px-3 py-2 text-sm font-medium text-vida-700 border border-vida-300 rounded-lg hover:bg-vida-50 disabled:opacity-50">Convertir en cliente</button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
            <button onClick={submit} disabled={busy} className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Propuestas comerciales (B2) ----------

const PROP_STATUS_LABEL: Record<ProposalStatus, string> = {
  draft: 'Borrador', sent: 'Enviada', accepted: 'Aceptada', rejected: 'Rechazada', expired: 'Caducada',
};
const PROP_STATUS_COLOR: Record<ProposalStatus, string> = {
  draft: 'bg-gray-100 text-gray-600', sent: 'bg-sky-100 text-sky-700', accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700', expired: 'bg-amber-100 text-amber-700',
};
const PROP_STATUSES: ProposalStatus[] = ['draft', 'sent', 'accepted', 'rejected', 'expired'];

const money = (n: number, currency = 'EUR') => {
  try { return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(n || 0); }
  catch { return `${(n || 0).toFixed(2)} ${currency}`; }
};

type LineForm = { concept: string; description: string; quantity: number; unitPrice: number; discountPct: number; taxPct: number };
const emptyLine = (): LineForm => ({ concept: '', description: '', quantity: 1, unitPrice: 0, discountPct: 0, taxPct: 21 });

// Mismos cálculos que el backend (solo previsualización; el backend recalcula al guardar).
function previewTotals(lines: LineForm[]) {
  let subtotal = 0, taxTotal = 0;
  for (const l of lines) {
    const gross = (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0);
    const net = gross - gross * ((Number(l.discountPct) || 0) / 100);
    subtotal += net; taxTotal += net * ((Number(l.taxPct) || 0) / 100);
  }
  const r2 = (n: number) => Math.round(n * 100) / 100;
  return { subtotal: r2(subtotal), taxTotal: r2(taxTotal), total: r2(subtotal + taxTotal) };
}

function ProposalsSection({ contactId }: { contactId: string }) {
  const canManage = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.CRM_MANAGE));
  const [list, setList] = useState<CrmProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CrmProposal | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try { const r = await crmApi.listProposals(contactId); setList((r.data.proposals || []) as CrmProposal[]); }
    catch { /* ficha nueva / sin permiso: silencioso */ } finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [contactId]);

  const setStatus = async (p: CrmProposal, status: ProposalStatus) => {
    setList((prev) => prev.map((x) => (x._id === p._id ? { ...x, status } : x))); // optimista
    try { await crmApi.setProposalStatus(p._id, status); }
    catch { toast.error('No se pudo cambiar el estado'); void load(); }
  };
  const remove = async (p: CrmProposal) => {
    if (!confirm(`¿Eliminar la propuesta nº ${p.number}?`)) return;
    try { await crmApi.removeProposal(p._id); toast.success('Propuesta eliminada'); void load(); }
    catch { toast.error('No se pudo eliminar'); }
  };

  return (
    <div className="mt-5 border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Propuestas ({list.length})</h3>
        {canManage && (
          <button onClick={() => setEditing('new')} className="inline-flex items-center gap-1 text-sm font-medium text-vida-700 hover:bg-vida-50 px-2 py-1 rounded">
            <PlusIcon className="w-4 h-4" /> Nueva propuesta
          </button>
        )}
      </div>
      {loading ? (
        <p className="text-xs text-gray-400 mt-2">Cargando…</p>
      ) : list.length === 0 ? (
        <p className="text-xs text-gray-400 mt-2">Aún no hay propuestas para este cliente.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {list.map((p) => (
            <li key={p._id} className="flex items-center gap-2 text-sm rounded px-2 py-1.5 hover:bg-gray-50">
              <span className="text-xs text-gray-400 font-mono w-10 flex-shrink-0">#{p.number}</span>
              <button onClick={() => canManage && setEditing(p)} className="flex-1 min-w-0 text-left truncate text-gray-800 hover:text-vida-700">
                {p.title}
              </button>
              <span className="text-gray-700 font-medium flex-shrink-0">{money(p.total, p.currency)}</span>
              {canManage ? (
                <select value={p.status} onChange={(e) => void setStatus(p, e.target.value as ProposalStatus)}
                  className={`text-[11px] rounded border-gray-200 py-0.5 pr-6 ${PROP_STATUS_COLOR[p.status]}`}>
                  {PROP_STATUSES.map((s) => <option key={s} value={s}>{PROP_STATUS_LABEL[s]}</option>)}
                </select>
              ) : (
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${PROP_STATUS_COLOR[p.status]}`}>{PROP_STATUS_LABEL[p.status]}</span>
              )}
              {canManage && (
                <button onClick={() => void remove(p)} className="p-1 text-gray-300 hover:text-red-600 flex-shrink-0"><TrashIcon className="w-4 h-4" /></button>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <ProposalEditor
          contactId={contactId}
          proposal={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
    </div>
  );
}

function ProposalEditor({ contactId, proposal, onClose, onSaved }: {
  contactId: string; proposal: CrmProposal | null; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!proposal;
  const [title, setTitle] = useState(proposal?.title || '');
  const [currency, setCurrency] = useState(proposal?.currency || 'EUR');
  const [validUntil, setValidUntil] = useState(proposal?.validUntil ? proposal.validUntil.slice(0, 10) : '');
  const [notes, setNotes] = useState(proposal?.notes || '');
  const [terms, setTerms] = useState(proposal?.terms || '');
  const [lines, setLines] = useState<LineForm[]>(
    proposal?.lines?.length
      ? proposal.lines.map((l: CrmProposalLine) => ({ concept: l.concept, description: l.description || '', quantity: l.quantity, unitPrice: l.unitPrice, discountPct: l.discountPct, taxPct: l.taxPct }))
      : [emptyLine()]
  );
  const [busy, setBusy] = useState(false);

  const totals = previewTotals(lines);
  const updateLine = (i: number, patch: Partial<LineForm>) => setLines((l) => l.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const numOr = (v: string) => (v === '' ? 0 : Number(v));

  const save = async () => {
    const cleanLines = lines.filter((l) => l.concept.trim());
    if (!title.trim()) { toast.error('Ponle un título a la propuesta'); return; }
    if (cleanLines.length === 0) { toast.error('Añade al menos una línea con concepto'); return; }
    setBusy(true);
    try {
      const data = {
        contactId, title: title.trim(), currency,
        lines: cleanLines.map((l) => ({ concept: l.concept.trim(), description: l.description || undefined, quantity: Number(l.quantity) || 0, unitPrice: Number(l.unitPrice) || 0, discountPct: Number(l.discountPct) || 0, taxPct: Number(l.taxPct) || 0 })),
        validUntil: validUntil || undefined, notes: notes || undefined, terms: terms || undefined,
      };
      if (isEdit && proposal) await crmApi.updateProposal(proposal._id, data);
      else await crmApi.createProposal(data);
      toast.success(isEdit ? 'Propuesta guardada' : 'Propuesta creada');
      onSaved();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo guardar la propuesta';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className={`${cardClass} w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto`}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {isEdit ? `Editar propuesta nº ${proposal!.number}` : 'Nueva propuesta'}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2"><label className={labelClass}>Título</label><input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div><label className={labelClass}>Moneda</label>
            <select className={inputClass} value={currency} onChange={(e) => setCurrency(e.target.value)}>
              <option value="EUR">EUR</option><option value="USD">USD</option><option value="GBP">GBP</option>
            </select>
          </div>
          <div><label className={labelClass}>Válida hasta</label><input type="date" className={inputClass} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></div>
        </div>

        {/* Líneas */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-900">Líneas</p>
            <button onClick={() => setLines((l) => [...l, emptyLine()])} className="inline-flex items-center gap-1 text-sm text-vida-700 hover:bg-vida-50 px-2 py-1 rounded"><PlusIcon className="w-4 h-4" /> Añadir línea</button>
          </div>
          <div className="space-y-2">
            {lines.map((l, i) => {
              const gross = (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0);
              const net = gross - gross * ((Number(l.discountPct) || 0) / 100);
              return (
                <div key={i} className="border border-gray-200 rounded-lg p-2.5">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-12 sm:col-span-5"><label className="text-[11px] text-gray-500">Concepto</label><input className={inputClass} value={l.concept} onChange={(e) => updateLine(i, { concept: e.target.value })} /></div>
                    <div className="col-span-4 sm:col-span-2"><label className="text-[11px] text-gray-500">Cant.</label><input type="number" min={0} step="any" className={inputClass} value={l.quantity} onChange={(e) => updateLine(i, { quantity: numOr(e.target.value) })} /></div>
                    <div className="col-span-4 sm:col-span-2"><label className="text-[11px] text-gray-500">Precio</label><input type="number" min={0} step="any" className={inputClass} value={l.unitPrice} onChange={(e) => updateLine(i, { unitPrice: numOr(e.target.value) })} /></div>
                    <div className="col-span-2 sm:col-span-1"><label className="text-[11px] text-gray-500">Dto%</label><input type="number" min={0} max={100} step="any" className={inputClass} value={l.discountPct} onChange={(e) => updateLine(i, { discountPct: numOr(e.target.value) })} /></div>
                    <div className="col-span-2 sm:col-span-1"><label className="text-[11px] text-gray-500">IVA%</label><input type="number" min={0} max={100} step="any" className={inputClass} value={l.taxPct} onChange={(e) => updateLine(i, { taxPct: numOr(e.target.value) })} /></div>
                    <div className="col-span-12 sm:col-span-1 flex items-center justify-between sm:justify-center">
                      <span className="text-sm font-medium text-gray-700 sm:hidden">{money(net, currency)}</span>
                      <button onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} className="p-1 text-gray-300 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="hidden sm:block text-right text-xs text-gray-500 mt-1">Importe línea: <span className="font-medium text-gray-700">{money(net, currency)}</span></div>
                  <input className={`${inputClass} mt-2`} placeholder="Descripción (opcional)" value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Totales */}
        <div className="mt-4 flex justify-end">
          <div className="w-full sm:w-64 text-sm space-y-1">
            <div className="flex justify-between text-gray-600"><span>Base imponible</span><span>{money(totals.subtotal, currency)}</span></div>
            <div className="flex justify-between text-gray-600"><span>IVA</span><span>{money(totals.taxTotal, currency)}</span></div>
            <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-200 pt-1"><span>Total</span><span>{money(totals.total, currency)}</span></div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div><label className={labelClass}>Notas</label><textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <div><label className={labelClass}>Términos y condiciones</label><textarea className={inputClass} rows={2} value={terms} onChange={(e) => setTerms(e.target.value)} /></div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Comunicaciones / timeline (B3) ----------

const COMM_TYPES: CommunicationType[] = ['call', 'email', 'note', 'whatsapp', 'meeting', 'promo'];
const COMM_TYPE_LABEL: Record<CommunicationType, string> = {
  call: 'Llamada', email: 'Email', note: 'Nota', whatsapp: 'WhatsApp', meeting: 'Reunión', promo: 'Promoción',
};
const COMM_TYPE_ICON: Record<CommunicationType, string> = {
  call: '📞', email: '✉️', note: '📝', whatsapp: '💬', meeting: '🤝', promo: '🏷️',
};
const COMM_DIR_LABEL: Record<CommunicationDirection, string> = { in: 'Entrante', out: 'Saliente', internal: 'Interna' };

const fmtDateTime = (iso: string) => {
  try { return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
// value para <input type="datetime-local"> (hora local, sin zona).
const toLocalInput = (iso?: string) => {
  const d = iso ? new Date(iso) : new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};

function CommunicationsSection({ contactId }: { contactId: string }) {
  const canManage = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.CRM_MANAGE));
  const [list, setList] = useState<CrmCommunication[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ type: 'note' as CommunicationType, direction: 'internal' as CommunicationDirection, occurredAt: toLocalInput(), subject: '', body: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await crmApi.listCommunications(contactId); setList((r.data.communications || []) as CrmCommunication[]); }
    catch { /* silencioso */ } finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [contactId]);

  const resetForm = () => setForm({ type: 'note', direction: 'internal', occurredAt: toLocalInput(), subject: '', body: '' });

  const add = async () => {
    if (!form.subject.trim() && !form.body.trim()) { toast.error('Añade un asunto o una descripción'); return; }
    setBusy(true);
    try {
      await crmApi.createCommunication({
        contactId, type: form.type, direction: form.direction,
        occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
        subject: form.subject.trim() || undefined, body: form.body.trim() || undefined,
      });
      toast.success('Interacción registrada');
      resetForm(); setAdding(false); void load();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo registrar';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const remove = async (c: CrmCommunication) => {
    if (!confirm('¿Eliminar esta interacción del historial?')) return;
    try { await crmApi.removeCommunication(c._id); toast.success('Eliminada'); void load(); }
    catch { toast.error('No se pudo eliminar'); }
  };

  return (
    <div className="mt-5 border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Historial de interacciones ({list.length})</h3>
        {canManage && !adding && (
          <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 text-sm font-medium text-vida-700 hover:bg-vida-50 px-2 py-1 rounded">
            <PlusIcon className="w-4 h-4" /> Registrar interacción
          </button>
        )}
      </div>

      {adding && (
        <div className="mt-3 border border-gray-200 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select className={inputClass} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as CommunicationType })}>
              {COMM_TYPES.map((t) => <option key={t} value={t}>{COMM_TYPE_ICON[t]} {COMM_TYPE_LABEL[t]}</option>)}
            </select>
            <select className={inputClass} value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value as CommunicationDirection })}>
              <option value="internal">Interna</option><option value="in">Entrante</option><option value="out">Saliente</option>
            </select>
            <input type="datetime-local" className={inputClass} value={form.occurredAt} onChange={(e) => setForm({ ...form, occurredAt: e.target.value })} />
          </div>
          <input className={inputClass} placeholder="Asunto" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <textarea className={inputClass} rows={2} placeholder="Descripción / notas" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <div className="flex justify-end gap-2">
            <button onClick={() => { setAdding(false); resetForm(); }} disabled={busy} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
            <button onClick={add} disabled={busy} className="px-3 py-1.5 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">{busy ? 'Guardando…' : 'Registrar'}</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-gray-400 mt-2">Cargando…</p>
      ) : list.length === 0 ? (
        <p className="text-xs text-gray-400 mt-2">Sin interacciones registradas todavía.</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {list.map((c) => (
            <li key={c._id} className="flex gap-2.5">
              <div className="flex-shrink-0 text-lg leading-none mt-0.5" title={COMM_TYPE_LABEL[c.type]}>{COMM_TYPE_ICON[c.type]}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-gray-700">{COMM_TYPE_LABEL[c.type]}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{COMM_DIR_LABEL[c.direction]}</span>
                  <span className="text-[11px] text-gray-400">{fmtDateTime(c.occurredAt)}</span>
                  {canManage && (
                    <button onClick={() => void remove(c)} className="ml-auto p-0.5 text-gray-300 hover:text-red-600"><TrashIcon className="w-3.5 h-3.5" /></button>
                  )}
                </div>
                {c.subject && <p className="text-sm font-medium text-gray-900 mt-0.5 truncate">{c.subject}</p>}
                {c.body && <p className="text-sm text-gray-600 whitespace-pre-wrap mt-0.5">{c.body}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Actividad unificada + enlace planificador (B4) ----------

const APPT_KIND_LABEL: Record<string, string> = {
  work: 'Cita / servicio', presence: 'Presencia', absence: 'Ausencia', meeting: 'Reunión',
};
const fmtRange = (start: string, end: string) => {
  try {
    const s = new Date(start), e = new Date(end);
    const d = s.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    const hs = s.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const he = e.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    return `${d} · ${hs}–${he}`;
  } catch { return `${start}–${end}`; }
};

// Muestra lo que solo el cruce entre BDs aporta: métricas de ecommerce + citas
// del planificador ligadas a este cliente. Propuestas y comunicaciones ya tienen
// sus propias secciones editables debajo (no se duplican aquí).
function ActivitySection({ contactId }: { contactId: string }) {
  const [data, setData] = useState<CrmActivityResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    crmApi.getActivity(contactId)
      .then((r) => { if (alive) setData(r.data as CrmActivityResponse); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [contactId]);

  if (loading || !data) return null;

  const appts = data.timeline.filter(
    (e): e is Extract<CrmActivityEntry, { source: 'appointment' }> => e.source === 'appointment'
  );
  const m = data.metrics;
  const hasMetrics = !!(data.linkedToCustomer && m && (m.total_orders || m.total_spent));
  if (!hasMetrics && appts.length === 0) return null; // nada cross-DB que mostrar

  return (
    <div className="mt-5 border-t border-gray-200 pt-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Actividad del cliente</h3>

      {hasMetrics && m && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className="text-[11px] text-gray-500">Pedidos</p>
            <p className="text-lg font-semibold text-gray-900">{m.total_orders ?? 0}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className="text-[11px] text-gray-500">Total gastado</p>
            <p className="text-lg font-semibold text-gray-900">{money(m.total_spent || 0)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className="text-[11px] text-gray-500">Último pedido</p>
            <p className="text-sm font-medium text-gray-700 mt-1">{m.last_order_at ? new Date(m.last_order_at).toLocaleDateString('es-ES') : '—'}</p>
          </div>
        </div>
      )}

      {appts.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Citas y reservas (planificador)</p>
          <ul className="space-y-1.5">
            {appts.slice(0, 12).map((a) => (
              <li key={a.id} className="flex items-center gap-2 text-sm rounded px-2 py-1.5 hover:bg-gray-50">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 flex-shrink-0">{APPT_KIND_LABEL[a.itemKind || 'work'] || a.itemKind}</span>
                <span className="text-gray-700 flex-shrink-0">{fmtRange(a.start, a.end)}</span>
                {(a.title || a.notes) && <span className="text-gray-500 truncate">— {a.title || a.notes}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------- Host reutilizable de la ficha (para abrir el MISMO modal desde otras
// vistas, p.ej. el Pipeline, sin navegar ni duplicar el ContactModal) ----------

export type ContactModalTarget =
  | { mode: 'id'; id: string }
  | { mode: 'new'; type: 'lead' | 'customer' };

export function ContactModalHost({ target, onClose, onSaved, hideAi }: {
  target: ContactModalTarget; onClose: () => void; onSaved: () => void; hideAi?: boolean;
}) {
  const [editing, setEditing] = useState<EditState>(null);
  const [history, setHistory] = useState<EditState[]>([]);
  const [companies, setCompanies] = useState<CrmContact[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const comps = await crmApi.listContacts({ kind: 'company', limit: 500 }).catch(() => ({ data: { contacts: [] } }));
      if (!alive) return;
      setCompanies((comps.data.contacts || []) as unknown as CrmContact[]);
      if (target.mode === 'id') {
        try {
          const res = await crmApi.getContact(target.id);
          if (alive) setEditing({ mode: 'edit', contact: res.data.contact });
        } catch { toast.error('No se pudo abrir la ficha'); if (alive) onClose(); }
      } else {
        setEditing({ mode: 'new', kind: 'person', prefill: { kind: 'person', type: target.type, source: 'manual' } });
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openContactById = async (id: string) => {
    try {
      const res = await crmApi.getContact(id);
      setHistory((h) => [...h, editing]);
      setEditing({ mode: 'edit', contact: res.data.contact });
    } catch { toast.error('No se pudo abrir la ficha'); }
  };
  const back = () => {
    if (history.length === 0) { onClose(); return; }
    setEditing(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
  };

  if (!editing) return null;
  return (
    <ContactModal
      key={(editing.mode === 'edit' ? editing.contact._id : 'new') + '-' + history.length}
      state={editing}
      companies={companies}
      canBack={history.length > 0}
      onBack={back}
      onOpenContact={openContactById}
      onClose={onClose}
      onSaved={onSaved}
      hideAi={hideAi}
    />
  );
}
